// packages/web/src/services/api.client.ts
// Central HTTP client. setToken() is called by AuthContext on login/logout.
// ================================================================

import type { ApiResponse } from '@hr-system/shared';

const BASE_URL = import.meta.env.VITE_API_URL ?? 'http://localhost:4000';

// ── Token store ───────────────────────────────────────────────────
let _token: string | null = null;

interface RequestOptions {
  params?: Record<string, string | number | boolean | undefined | null>;
  signal?: AbortSignal;
  headers?: Record<string, string>;
  responseType?: string;
}

function buildQuery(params?: RequestOptions['params']): string {
  if (!params) return '';
  const entries = Object.entries(params).filter(
    ([, v]) => v !== undefined && v !== null && v !== '',
  );
  if (!entries.length) return '';
  return '?' + entries.map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(String(v))}`).join('&');
}

async function request<T>(
  method: string,
  path: string,
  body?: unknown,
  options?: RequestOptions,
): Promise<ApiResponse<T>> {
  const url = `${BASE_URL}/api/v1${path}${buildQuery(options?.params)}`;
  const isFormData = typeof FormData !== 'undefined' && body instanceof FormData;
  const headers: Record<string, string> = isFormData ? {} : { 'Content-Type': 'application/json' };
  if (_token) headers['Authorization'] = `Bearer ${_token}`;
  if (options?.headers) Object.assign(headers, options.headers);

  const res = await fetch(url, {
    method,
    headers,
    body: isFormData ? (body as FormData) : (body !== undefined ? JSON.stringify(body) : undefined),
    signal: options?.signal,
  });

  // Guard: check HTTP status before parsing JSON
  if (!res.ok) {
    let errorMsg = `HTTP ${res.status}`;
    try {
      const errJson = await res.json();
      const raw = errJson?.error ?? errJson?.message ?? errorMsg;
      errorMsg = typeof raw === 'string' ? raw : raw?.message ?? errorMsg;
    } catch { /* non-JSON body */ }
    return { success: false, data: undefined as unknown as T, error: { code: 'HTTP_ERROR', message: errorMsg } } satisfies ApiResponse<T>;
  }

  const json: ApiResponse<T> = await res.json();
  if (!json.success) {
    return json;
  }
  return json;
}

export const apiClient = {
  /** Set the JWT token (called by AuthContext on login / logout) */
  setToken(token: string | null): void { _token = token; },
  getToken(): string | null { return _token; },

  get<T>(path: string, options?: RequestOptions): Promise<ApiResponse<T>> {
    return request<T>('GET', path, undefined, options);
  },
  post<T>(path: string, body?: unknown, options?: RequestOptions): Promise<ApiResponse<T>> {
    return request<T>('POST', path, body, options);
  },
  patch<T>(path: string, body?: unknown, options?: RequestOptions): Promise<ApiResponse<T>> {
    return request<T>('PATCH', path, body, options);
  },
  put<T>(path: string, body?: unknown, options?: RequestOptions): Promise<ApiResponse<T>> {
    return request<T>('PUT', path, body, options);
  },
  delete<T>(path: string, options?: RequestOptions): Promise<ApiResponse<T>> {
    return request<T>('DELETE', path, undefined, options);
  },
};

export default apiClient;